/**
 * =================================================================
 * SCRIPT-NAME:   SendEmail.js
 * BESCHREIBUNG:   UI-Komponente zum Filtern von Kontakten und Versenden
 * von Massen-E-Mails.
 *
 * VERSION:       v1.2.i1
 * DATUM:         2025-08-11 18:11:00
 *
 * ÄNDERUNGEN ZUR VORVERSION (v1.1.i1):
 * - Implementierung des fetch-Aufrufs an das Backend-Skript
 * 'send_bulk_email.php' in der handleSendEmails-Funktion.
 * - Robuste Fehler- und Erfolgsbehandlung für den API-Aufruf.
 * =================================================================
 */
import React, { useState } from 'react';
import BackButton from "./BackButton";
import "./AdminDashboard.css";
import "./Contacts.css";
import "./SendEmail.css";

export default function SendEmail() {
  const [filter, setFilter] = useState('');
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState({});
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false); // To disable button during sending
  const token = localStorage.getItem("token");

  const filterOptions = [
    { value: '', label: 'Filter auswählen...' },
    { value: 'it_booked_not_watched', label: 'IT: Angemeldet, aber nicht teilgenommen' },
    { value: 'it_watched_long', label: 'IT: Teilgenommen (>= 35min)' },
    { value: 'eg_booked', label: 'EG: Gespräch gebucht' },
  ];

  const handleFilterChange = (e) => {
    const selectedFilter = e.target.value;
    setFilter(selectedFilter);
    setContacts([]);
    setSelectedContacts({});

    if (selectedFilter) {
      setMessage(`Lade Kontakte für Filter: ${selectedFilter}...`);
      
      fetch("https://freiseinapp.de/api/get_filtered_contacts.php", {
          method: "POST",
          headers: {
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json"
          },
          body: JSON.stringify({ filter: selectedFilter })
      })
      .then(response => response.json())
      .then(data => {
          if (data.success) {
              setContacts(data.contacts);
              const initialSelection = data.contacts.reduce((acc, contact) => {
                  acc[contact.id] = true;
                  return acc;
              }, {});
              setSelectedContacts(initialSelection);
              setMessage(`${data.contacts.length} Kontakte geladen. Bitte Auswahl prüfen.`);
          } else {
              setMessage(`❌ Fehler: ${data.message}`);
          }
      })
      .catch(() => {
          setMessage('❌ Netzwerkfehler beim Laden der Kontakte.');
      });

    } else {
      setMessage('');
    }
  };

  const handleCheckboxChange = (contactId) => {
    setSelectedContacts(prev => ({
      ...prev,
      [contactId]: !prev[contactId]
    }));
  };

  const handleSendEmails = () => {
    const finalContactIds = Object.keys(selectedContacts).filter(id => selectedContacts[id]);
    if (finalContactIds.length === 0) {
      setMessage('❌ Bitte wählen Sie mindestens einen Kontakt aus.');
      return;
    }
    if (!subject || !body) {
      setMessage('❌ Bitte geben Sie einen Betreff und einen Nachrichtentext ein.');
      return;
    }

    setIsSending(true);
    setMessage(`Sende E-Mails an ${finalContactIds.length} Kontakte...`);
    
    // --- NEU: Echter API-Aufruf an das Backend ---
    fetch("https://freiseinapp.de/api/send_bulk_email.php", {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            contactIds: finalContactIds,
            subject: subject,
            body: body
        })
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) {
            setMessage(`✅ E-Mail-Versand an ${data.sentCount} von ${finalContactIds.length} Kontakten erfolgreich gestartet.`);
        } else {
            setMessage(`❌ Fehler beim E-Mail-Versand: ${data.message}`);
        }
    })
    .catch(() => {
        setMessage('❌ Netzwerkfehler beim Senden der E-Mails.');
    })
    .finally(() => {
        setIsSending(false); // Re-enable the button after the process is complete
    });
    // --- ENDE NEU ---
  };

  return (
    <div className="dashboard-page-wrapper">
      <div className="dashboard-content-box">
        <header className="dashboard-header">
          <h2 className="dashboard-title">✉️ E-Mail senden</h2>
        </header>

        {message && <p className={message.startsWith('❌') ? "contacts-error" : "contacts-success"}>{message}</p>}

        <div className="send-email-container">
          {/* SECTION 1: FILTERING */}
          <section className="email-section">
            <h3>1. Kontakte auswählen</h3>
            <div className="filter-controls">
              <label htmlFor="contact-filter">Filter anwenden:</label>
              <select id="contact-filter" value={filter} onChange={handleFilterChange}>
                {filterOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            {contacts.length > 0 && (
              <div className="contact-list">
                <h4>Kontakte für den Versand (bitte Auswahl prüfen):</h4>
                {contacts.map(contact => (
                  <div key={contact.id} className="contact-list-item">
                    <input
                      type="checkbox"
                      id={`contact-${contact.id}`}
                      checked={!!selectedContacts[contact.id]}
                      onChange={() => handleCheckboxChange(contact.id)}
                    />
                    <label htmlFor={`contact-${contact.id}`}>
                      {contact.vorname} {contact.nachname} ({contact.email})
                    </label>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* SECTION 2: EMAIL EDITOR */}
          <section className="email-section">
            <h3>2. E-Mail verfassen</h3>
            <div className="email-form">
              <label htmlFor="subject">Betreff:</label>
              <input
                type="text"
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <label htmlFor="body">Nachricht (Platzhalter wie {`{vorname}`} werden auf dem Server ersetzt):</label>
              <textarea
                id="body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              ></textarea>
            </div>
          </section>

          {/* SECTION 3: SEND BUTTON */}
          <section>
            <button onClick={handleSendEmails} className="send-button" disabled={isSending}>
              {isSending ? 'Sende...' : `E-Mails jetzt an ${Object.values(selectedContacts).filter(Boolean).length} Kontakte senden`}
            </button>
          </section>
        </div>

        <div className="dashboard-footer">
          <BackButton to="/admin" />
        </div>
      </div>
    </div>
  );
}
